import React, { useState, useEffect, useRef } from 'react';
import { FaICursor, FaPlay, FaPython, FaStop, FaTerminal } from 'react-icons/fa';
import { PyodideClient } from '../../../pyodide/PyodideProvider';
import { MonacoTipTap } from './runnableKarel/TipTapCodeEditor';


// tiptap
import { NodeViewWrapper } from '@tiptap/react'
import { Node, mergeAttributes } from '@tiptap/core'
import { ReactNodeViewRenderer } from '@tiptap/react'
import styled from 'styled-components';

const MAX_OUTPUT_LINES = 200

export const ConsoleDemo = Node.create({
  name: 'console-demo',
  group: 'block',
  atom: true,
  draggable: true,
  addAttributes() {

    return {
      // we need to make output a state (not an attribute)
      // so that a big for loop doesn't cause too many server writes
      // If we do want it to have persistent output, we can debounce it.
      // Another note: for textbooks (which also use tiptap) not showing
      // output at the start can be a feature.
      code: {
        default: '# your code here\n',
      }
    }
  },

  parseHTML() {

    return [
      {
        tag: 'console-demo',
      },
    ]
  },

  renderHTML({ HTMLAttributes }) {

    return ['console-demo', mergeAttributes(HTMLAttributes)]
  },

  addNodeView() {
    const node = ReactNodeViewRenderer(RenderRunableCode)
    return node

  },
})

export const RenderRunableCode = (props) => {
  // props for keeping track of the output. 
  const outputRef = useRef(null);

  // for controlling inputs
  const [isInput, setIsInput] = useState(false);
  const [inputValue, setInputValue] = useState(''); // State to hold the input value
  const [resolveInputPromise, setResolveInputPromise] = useState(null); // State to hold the function that resolves the promise
  const inputRef = useRef(null);



  const [isRunning, setIsRunning] = useState(false);
  const [output, setViewedOutput] = useState('');
  const [showCode, setShowCode] = useState(false);
  const [showOutput, setShowOutput] = useState(false);
  // we should not need this. workaround beacuse "editable" is not reactive
  const [isEditable, setIsEditable] = useState(props.editor.isEditable)
  const pyodideClientRef = useRef(null);

  // Update the onInputEntered to resolve the promise
  const onInputEntered = (input) => {
    if (resolveInputPromise) {
      resolveInputPromise(input);
      setResolveInputPromise(null); // Reset for the next input
      setInputValue(''); // Clear the input field
    }
  };

  const onInputChange = (newValue) => {
    setInputValue(newValue);
    // also increase the input width if necessary
    if (inputRef.current) {
      const fontSizeToWidth = 1.6 // for our monospace 
      const computedStyle = window.getComputedStyle(inputRef.current);
      const fontSize = computedStyle.getPropertyValue('font-size');
      // strip the px
      const fontSizeNum = parseFloat(fontSize.slice(0, -2))
      const charWidth = fontSizeNum / fontSizeToWidth
      const newWidth = Math.max(50, newValue.length * charWidth)
      inputRef.current.style.width = `${newWidth}px`
    }
  }

  const setPyodideHandlers = () => {
    pyodideClientRef.current.setHandlers(
      () => {
        // Func called on end
        setIsRunning(false)
      },
      (stdout) => {
        // Func called on stdout
        appendOutput(stdout)
      },
      (stderr) => {
        // Func called on error
        appendOutput(stderr);
        setIsRunning(false)
      },
      (inputPrompt) => {
        setIsInput(true);
        // Set up a new promise that will be resolved when the input is entered
        const promise = new Promise((resolve) => {
          setResolveInputPromise(() => resolve);
        });
        return promise.then((input) => {
          setIsInput(false); // Reset input state
          appendOutput(input, false); // Append the input to the output (without a newline)
          return input; // Return the input value that was set
        });
      },
      null,
      null
    )
  }

  let runCode = () => {
    setOutput('');
    let pyodideClient = pyodideClientRef.current;
    setPyodideHandlers();
    const code = getCode();
    setIsRunning(true)
    pyodideClient.runCode(code, { name: "main.py" }, false, 500);
    setShowOutput(true);
  };


  useEffect(() => {
    pyodideClientRef.current = new PyodideClient();
    pyodideClientRef.current.setHandlers(
      () => {
        // Func called on end
        setIsRunning(false)
      },
      (stdout) => {
        // Func called on stdout
        appendOutput(stdout)
      },
      (stderr) => {
        // Func called on error
        appendOutput(stderr);
        setIsRunning(false)
      },
      () => {/*Input run*/ },
      null
    )
  }, [])

  // this is fired by writing an empty string each time editable changes :(
  props.editor.on('transaction', () => {
    setIsEditable(props.editor.isEditable)
  })


  const getCode = () => {
    return props.node.attrs.code || ""
  };

  const setOutput = (newValue) => {
    let lines = newValue.split('\n')
    // the off by one is for the final return
    let nLines = lines.length - 1
    if (nLines > MAX_OUTPUT_LINES) {
      // off by one here for the same reason
      let toKeep = lines.slice(-(MAX_OUTPUT_LINES + 1))
      newValue = `last ${MAX_OUTPUT_LINES} lines...\n` + toKeep.join('\n')
    }
    setViewedOutput(newValue)
    // scroll to the bottom of the output
    if (outputRef.current) {
      outputRef.current.scrollTop = outputRef.current.scrollHeight;
    }
  }

  var currOutput = ''
  const appendOutput = (newValue, nl = true) => {
    if (nl && currOutput.length > 0) {
      currOutput = `${currOutput}\n${newValue}`;
    } else {
      currOutput = `${currOutput}${newValue}`;
    }
    setOutput(currOutput)
  }

  const onValueChange = (newCode) => {
    props.updateAttributes({
      code: newCode
    })
  };

  return (
    <NodeViewWrapper data-drag-handle>
      <div
        style={{
          border: 'lightgrey',
          borderStyle: 'solid',
          borderWidth: '1px',
        }}
        spellCheck="false"
        draggable="true"
      >
        {isEditable && showCode && <MonacoTipTap
          mode="python"
          value={getCode()}
          onChange={(e) => onValueChange(e)}
          readOnly={false}
        />}
        {showOutput && (
          <TerminalOutput
            ref={outputRef}
            spellCheck="false"
            style={{
              backgroundColor: 'black',
              color: 'white',
              height: '200px',
              marginBottom: '0px',
              padding: '5px',
            }}
          >
            {output}
            {isInput && (
              <ConsoleInput
                autoFocus
                type="text"
                id="input"
                name="input"
                ref={inputRef}
                // on enter call onInputEntered (onkeypress is depricated)
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    onInputEntered(e.target.value)
                  }
                }}
                value={inputValue}
                onChange={(e) => onInputChange(e.target.value)}

              />
            )}
          </TerminalOutput>
        )}
        <RunButtonRow
          isRunning={isRunning}
          runCode={() => runCode()}
          showCode={showCode}
          setShowCode={setShowCode}
          pyodideClient={pyodideClientRef.current}
          resolveInputPromise={resolveInputPromise}
          setResolveInputPromise={setResolveInputPromise}
          isEditable={isEditable}
          disabled={false}
        />

      </div>
    </NodeViewWrapper>
  );
};

const RunButtonRow = ({
  isRunning,
  resolveInputPromise,
  setResolveInputPromise,
  pyodideClient,
  runCode,
  disabled,
  isEditable,
  showCode,
  setShowCode,
}) => {
  let runStopText = (
    <span>
      <FaPlay /> {'Run'}
    </span>
  );
  if (isRunning) {
    runStopText = (
      <span>
        <FaStop /> {'Stop'}
      </span>
    );
  }

  let showCodeBtnLabel = (
    <span>
      <FaPython /> Show
    </span>
  );
  if (showCode) {
    showCodeBtnLabel = (
      <span>
        <FaPython /> Hide
      </span>
    );
  }

  const onRunStopClick = () => {
    if (isRunning) {
      pyodideClient.raisePyStopFlag()
      if (resolveInputPromise) {
        resolveInputPromise(''); // Or some special value indicating a stop
        setResolveInputPromise(null);
      }
    } else {
      runCode();
    }
  };

  return (
    <>
      <button
        onClick={() => onRunStopClick()}
        className="btn btn-light btn-sm "
        style={{ width: '80px' }}
        disabled={disabled}
      >
        {runStopText}
      </button>

      {isEditable && <button
        onClick={() => setShowCode(!showCode)}
        style={{ width: '80px' }}
        className="btn btn-light btn-sm "
        disabled={disabled}
      >
        {showCodeBtnLabel}
      </button>}
    </>
  );
};

const TerminalOutput = styled.pre`
  `

const ConsoleInput = styled.input`
  background-color: black;
  color: white;
  border: none;
  width: 50px;
  flex-grow:1;
  margin: 0;
  font-size: 1em;
  &:focus {
    outline: none;
  }
`;